/**
 * HTML Sanitization Utility
 * Prevents XSS attacks when rendering user-generated HTML content
 */

// List of allowed HTML tags for user content
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'a',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'div',
  'span',
];

// Allowed attributes per tag
const ALLOWED_ATTRS = {
  a: ['href', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height'],
  '*': ['class', 'id'],
};

// Dangerous patterns to remove
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi, // onclick, onerror, etc.
  /data:/gi,
  /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
  /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
  /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi,
];

/**
 * Basic HTML sanitizer
 * For production, consider using DOMPurify library instead
 * @param {string} html - Raw HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }

  let sanitized = html;

  // Remove dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Ensure all links open in new tab with security attributes
  sanitized = sanitized.replace(/<a\s+([^>]*?)>/gi, (match, attrs) => {
    // Add rel="noopener noreferrer" and target="_blank" if not present
    if (!attrs.includes('rel=')) {
      attrs += ' rel="noopener noreferrer"';
    }
    if (!attrs.includes('target=')) {
      attrs += ' target="_blank"';
    }
    return `<a ${attrs}>`;
  });

  return sanitized;
}

/**
 * Strip all HTML tags, leaving only text
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
export function stripHtml(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }
  return html.replace(/<[^>]*>/g, '');
}

/**
 * Sanitize plain text input (for forms, names, etc.)
 * Removes all HTML and dangerous characters
 * @param {string} input - Input string to sanitize
 * @returns {string} Sanitized plain text
 */
export function sanitizeInput(input) {
  if (!input || typeof input !== 'string') {
    return '';
  }

  let sanitized = input;

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');

  // Remove all HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  // Remove dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Trim whitespace
  sanitized = sanitized.trim();

  return sanitized;
}

/**
 * Truncate HTML content safely (doesn't break tags)
 * @param {string} html - HTML string
 * @param {number} maxLength - Maximum text length
 * @returns {string} Truncated text with ellipsis
 */
export function truncateHtml(html, maxLength = 200) {
  const text = stripHtml(html);
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength).trim() + '...';
}

export default sanitizeHtml;
